import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats as F } from "html5-qrcode";
import { X, Flashlight, FlashlightOff } from "lucide-react";

const FORMATOS = [F.QR_CODE, F.CODE_128, F.CODE_39, F.CODE_93, F.CODABAR, F.ITF, F.EAN_13, F.EAN_8, F.UPC_A, F.UPC_E, F.PDF_417, F.DATA_MATRIX, F.AZTEC];
// Recuadro ancho y bajo: un código de barras largo cabe completo aunque esté algo inclinado.
const recuadro = (w, h) => ({ width: Math.floor(w * 0.92), height: Math.max(90, Math.floor(Math.min(h * 0.38, w * 0.5))) });

let anterior = Promise.resolve();

/** Escáner de códigos de barras y QR (cámara trasera, enfoque continuo, linterna).
 *  continuo=true: no se cierra; llama onCode por cada código distinto (útil para cargar varios productos). */
export const Escaner = ({ onCode, onClose, titulo = "ESCANEAR GUÍA", placeholder = "Número de guía", continuo = false, children }) => {
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");
  const [luz, setLuz] = useState(null);
  const done = useRef(false);
  const ultimo = useRef({ t: "", at: 0 });
  const scRef = useRef(null);
  const cb = useRef(onCode);
  cb.current = onCode;
  useEffect(() => {
    done.current = false;
    let sc = null;
    let vivo = true;
    const parar = () => {
      try { if (sc && sc.isScanning) sc.getRunningTrackCameraCapabilities().torchFeature().apply(false).catch(() => {}); } catch { /* sin linterna */ }
      return (sc && sc.isScanning ? sc.stop().catch(() => {}) : Promise.resolve()).then(() => { try { sc && sc.clear(); } catch { /* */ } });
    };
    const leido = (txt) => {
      const t = txt.trim();
      if (done.current || !t) return;
      const now = Date.now();
      if (continuo) {
        if (ultimo.current.t === t && now - ultimo.current.at < 3000) return;
        if (now - ultimo.current.at < 700) return;
        ultimo.current = { t, at: now };
        if (navigator.vibrate) navigator.vibrate(80);
        try { Promise.resolve(cb.current(t)).catch(() => {}); } catch { /* el escáner sigue */ }
        return;
      }
      done.current = true;
      if (navigator.vibrate) navigator.vibrate(120);
      parar().finally(() => cb.current(t));
    };
    // Espera a que se libere la cámara de un escáner anterior (evita dos instancias en el mismo recuadro).
    const started = Promise.race([anterior, new Promise((r) => setTimeout(r, 2500))]).then(() => {
      if (!vivo) return null;
      sc = new Html5Qrcode("emp-scanner", { verbose: false, formatsToSupport: FORMATOS, experimentalFeatures: { useBarCodeDetectorIfSupported: true } });
      scRef.current = sc;
      const cfg = (vc) => ({ fps: 15, qrbox: recuadro, disableFlip: false, ...(vc ? { videoConstraints: vc } : {}) });
      // Varios intentos: algunos Samsung/Android rechazan resolución o enfoque pedidos; se reintenta con menos exigencias.
      const intentos = [
        () => sc.start({ facingMode: "environment" }, cfg({ facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 }, advanced: [{ focusMode: "continuous" }] }), leido),
        () => sc.start({ facingMode: "environment" }, cfg(null), leido),
        () => Html5Qrcode.getCameras().then((cs) => {
          const cam = cs.find((c) => /back|trasera|rear|environment|0/i.test(c.label)) || cs[cs.length - 1];
          if (!cam) throw new Error("sin camara");
          return sc.start(cam.id, cfg(null), leido);
        }),
      ];
      const probar = (i) => intentos[i]().catch((e) => {
        if (!vivo || i + 1 >= intentos.length) throw e;
        return (sc.isScanning ? sc.stop().catch(() => {}) : Promise.resolve()).then(() => probar(i + 1));
      });
      return probar(0).then(() => {
        try {
          const tf = sc.getRunningTrackCameraCapabilities().torchFeature();
          if (tf.isSupported()) { tf.apply(true).then(() => setLuz(true)).catch(() => setLuz(false)); } else setLuz(null);
        } catch { setLuz(null); }
      });
    }).catch((e) => done.current || setErr(/NotAllowed|Permission/i.test(String(e?.name || e))
      ? "El navegador no tiene permiso para usar la cámara. Toque el candado junto a la dirección y permita Cámara, o escriba el código abajo."
      : "No se pudo abrir la cámara. Cierre otras apps que usen la cámara e intente de nuevo, o escriba el código abajo."));
    return () => { vivo = false; done.current = true; anterior = started.then(parar, parar); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const linterna = async () => {
    try { await scRef.current.getRunningTrackCameraCapabilities().torchFeature().apply(!luz); setLuz(!luz); } catch { setErr("Este celular no permite encender la linterna desde el navegador."); }
  };
  const usarManual = () => {
    if (!continuo) done.current = true;
    cb.current(manual.trim());
    setManual("");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col" data-testid="emp-scanner-modal">
      <div className="h-16 px-4 flex items-center justify-between shrink-0">
        <div className="text-xl font-black">{titulo}</div>
        <div className="flex items-center gap-1">
          {luz !== null && <button onClick={linterna} className={`p-2 rounded-lg ${luz ? "bg-amber-400 text-black" : ""}`} aria-label="Linterna" data-testid="emp-scanner-linterna">{luz ? <FlashlightOff className="w-7 h-7" /> : <Flashlight className="w-7 h-7" />}</button>}
          <button onClick={onClose} className="p-2" data-testid="emp-scanner-close" aria-label="Cerrar"><X className="w-8 h-8" /></button>
        </div>
      </div>
      <div className="w-full flex-1 min-h-0 overflow-hidden bg-zinc-900 flex items-center">
        <div id="emp-scanner" className="w-full [&_video]:w-full [&_video]:h-auto" />
      </div>
      {err && <div className="p-3 text-base bg-red-700" data-testid="emp-scanner-error">{err}</div>}
      {children}
      <div className="p-4 space-y-3 bg-zinc-950 shrink-0">
        <div className="text-sm opacity-80">¿No lee el código? Escríbalo:</div>
        <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder={placeholder} inputMode="text"
          className="w-full h-14 rounded-lg px-4 text-2xl font-mono text-black" data-testid="emp-scanner-manual" />
        <button disabled={manual.trim().length < 3} onClick={usarManual}
          className="w-full h-14 rounded-lg bg-emerald-600 disabled:opacity-40 text-xl font-black uppercase" data-testid="emp-scanner-manual-ok">Usar este código</button>
      </div>
    </div>
  );
};
